import type { Directive } from 'vue'

import {
  SLIDE_IN_ON_SCROLL_DURATION_MS,
  SLIDE_IN_ON_SCROLL_EASING,
  SLIDE_IN_ON_SCROLL_KEYFRAMES,
  SLIDE_IN_ON_SCROLL_STAGGER_MS,
  SLIDE_IN_ON_SCROLL_VIEWPORT_AND_ABOVE_MARGIN,
  SLIDE_IN_ON_SCROLL_WAITING_ATTRIBUTE,
} from '@layers/web/constants/slide-in-on-scroll.ts'

const slideIn = (element: Element, delay: number, observer: IntersectionObserver): void => {
  observer.unobserve(element)

  const slide = element.animate(SLIDE_IN_ON_SCROLL_KEYFRAMES, {
    duration: SLIDE_IN_ON_SCROLL_DURATION_MS,
    delay,
    easing: SLIDE_IN_ON_SCROLL_EASING,
    fill: 'backwards',
  })

  slide.addEventListener('finish', () => {
    if (element.isConnected) {
      observer.observe(element)
    }
  })
}

const onIntersectionChange = (entries: IntersectionObserverEntry[], observer: IntersectionObserver): void => {
  entries
    .filter(({ isIntersecting }) => !isIntersecting)
    .forEach(({ target }) => target.setAttribute(SLIDE_IN_ON_SCROLL_WAITING_ATTRIBUTE, ''))

  entries
    .filter(({ isIntersecting, target }) => isIntersecting && target.hasAttribute(SLIDE_IN_ON_SCROLL_WAITING_ATTRIBUTE))
    .forEach(({ target }, index) => {
      target.removeAttribute(SLIDE_IN_ON_SCROLL_WAITING_ATTRIBUTE)
      slideIn(target, index * SLIDE_IN_ON_SCROLL_STAGGER_MS, observer)
    })
}

const slideInObserver = import.meta.client
  ? new IntersectionObserver(onIntersectionChange, { rootMargin: SLIDE_IN_ON_SCROLL_VIEWPORT_AND_ABOVE_MARGIN })
  : undefined

const vSlideInOnScroll: Directive<HTMLElement> = {
  mounted (element) {
    if (window.didHistoryPop || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    slideInObserver?.observe(element)
  },

  beforeUnmount (element) {
    slideInObserver?.unobserve(element)
  },
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('slide-in-on-scroll', vSlideInOnScroll)
})
